/**
 * 智能体用户知识库管理器
 * 负责管理智能体用户知识库的业务操作
 * @author Joey
 */

import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { PUSH_SETTINGS_TYPE, userManagementManager } from "./agent-dashboard-user-management-manager";
import { AgentUserKnowledgeFolderData, getAgentUserKnowledgeFolders } from "@/services/knowledge-get-agent-user-knowledge-folders";
import { knowledgeAddAgentUserKnowledgeFolder } from "@/services/knowledge-add-agent-user-knowledge-folders";
import { deleteAgentUserKnowledgeFolder } from "@/services/knowledge-delete-agent-user-knowledge-folders";
import { ItemDetailData, knowledgeGetFolderItems } from "@/services/knowledge-get-folder-items";
import { KnowledgeFolderData, knowledgeListFolders } from "@/services/knowledge-list-folders";


interface KnowledgeFolderSearchForm {
  page: number;
  pageSize: number;
  total: number;
}

type KnowledgeFoldersChangeEventDetail = {
  knowledgeFolders: KnowledgeFolderData[];
}
type KnowledgeFoldersChangeEvent = CustomEvent<KnowledgeFoldersChangeEventDetail>;

type KnowledgeFolderSearchFormChangeEventDetail = {
  knowledgeFolderSearchForm: KnowledgeFolderSearchForm;
}
type KnowledgeFolderSearchFormChangeEvent = CustomEvent<KnowledgeFolderSearchFormChangeEventDetail>;

type AgentUserKnowledgeChangeEventDetail = {
  agentUserKnowledge: AgentUserKnowledgeFolderData[];
}
type AgentUserKnowledgeChangeEvent = CustomEvent<AgentUserKnowledgeChangeEventDetail>;

type KnowledgeFolderItemsChangeEventDetail = {
  knowledgeFolderItems: ItemDetailData[];
}
type KnowledgeFolderItemsChangeEvent = CustomEvent<KnowledgeFolderItemsChangeEventDetail>;

export class AgentDashboardKnowledgeManager {
  private static instance: AgentDashboardKnowledgeManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_DASHBOARD_KNOWLEDGE";
  private agentId: string = '';
  private userId: string = '';
  // 智能体用户知识库列表
  private agentUserKnowledge: AgentUserKnowledgeFolderData[] = [];
  // 知识库目录列表
  private knowledgeFolders: KnowledgeFolderData[] = [];
  // 知识库目录请求表单
  private knowledgeFolderSearchForm: KnowledgeFolderSearchForm = {
    page: 1,
    pageSize: 1000,
    total: 0,
  }
  // 知识库文件夹内文件列表
  private knowledgeFolderItems: ItemDetailData[] = [];

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentDashboardKnowledgeManager {
    if (!AgentDashboardKnowledgeManager.instance) {
      AgentDashboardKnowledgeManager.instance = new AgentDashboardKnowledgeManager();
    }
    return AgentDashboardKnowledgeManager.instance;
  }

  // 初始化
  public async init({ agentId, userId }: { agentId: string, userId: string }) {
    this.setAgentId(agentId);
    this.setUserId(userId);
    try {
      // 初始化时，如果列表为空，才请求数据
      // if (this.getAgentUserMaterials().length === 0) {
      //   await this.fetchAgentUserMaterials({ agentId, userId });
      // }
    } catch (error) {
      console.error("Failed to init agent material folder list cache:", error);
      throw error;
    }
  }

  // 请求知识库目录数据（用于添加知识库模板列表）
  public async fetchKnowledgeFolders(page?: number, userId?: string) {
    try {
      const data = await knowledgeListFolders({
        page: page || this.knowledgeFolderSearchForm.page,
        pageSize: this.knowledgeFolderSearchForm.pageSize,
        userId: userId || "",
        agentId: this.getAgentId(),
      });

      this.setKnowledgeFolders(data.list);

    } catch (error) {
      console.error("Failed to fetch assets material list:", error);
      this.dispatchKnowledgeFoldersChange();
      throw error;
    }
  }

  // 请求智能体用户知识库
  public async fetchAgentUserKnowledge({
    agentId,
    userId,
    type,
  }: { agentId?: string, userId?: string, type?: number }) {
    try {
      const data = await getAgentUserKnowledgeFolders({
        agentId: agentId || this.getAgentId(),
        page: 1,
        pageSize: 1000,
        userId: userId || this.getUserId(),
        type: type || PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
      });
      this.setAgentUserKnowledge(data.list || []);
    } catch (error) {
      console.error("Failed to fetch agent user knowledge list:", error);
      this.dispatchAgentUserKnowledgeChange();
      throw error;
    }
  }

  // 请求智能体用户知识库指定文件夹内文件列表数据
  public async fetchKnowledgeFolderItems(folderId: number) {
    try {
      const data = await knowledgeGetFolderItems(folderId);
      const details = (data?.items && data.items.length) ?
        data.items.map(item => {
          return {
            ...item.itemDetail[0],
          }
        })
        : []
      this.setKnowledgeFolderItems(details);
    } catch (error) {
      console.error("Failed to fetch folder items list:", error);
      this.dispatchKnowledgeFolderItemsChange();
      throw error;
    }
  }

  // 创建智能体用户知识库
  public async agentUserKnowledgeCreate({
    type,
    folderId
  }: { type: number, folderId: number[] }) {
    try {
      const res = await knowledgeAddAgentUserKnowledgeFolder({
        agentId: this.getAgentId(),
        userId: this.getUserId(),
        folderId,
        type,
      });
      return res;
    } catch (error) {
      console.error("Failed to create agent user knowledge folder:", error);
      this.dispatchKnowledgeFoldersChange();
      throw error;
    }
  }

  // 删除智能体用户知识库
  public async agentUserKnowledgeDelete(id: number) {
    try {
      // 请求删除知识库目录
      await deleteAgentUserKnowledgeFolder({
        agentId: this.getAgentId(),
        userId: this.getUserId(),
        type: PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
        folderId: id,
      });
    } catch (error) {
      // 回退更新
      console.log(error)
      // 继续抛出异常
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }
  public getAgentId() {
    return this.agentId;
  }

  public setUserId(userId: string) {
    this.userId = userId;
  }
  public getUserId() {
    return this.userId;
  }

  public getKnowledgeFolders() {
    return this.knowledgeFolders;
  }
  public setKnowledgeFolders(list: KnowledgeFolderData[]) {
    this.knowledgeFolders = list;
    this.dispatchKnowledgeFoldersChange();
  }

  public setKnowledgeFolderSearchForm(form: KnowledgeFolderSearchForm) {
    this.knowledgeFolderSearchForm = form;
    this.dispatchKnowledgeFolderSearchFormChange();
  }
  public getKnowledgeFolderSearchForm() {
    return this.knowledgeFolderSearchForm;
  }

  public getAgentUserKnowledge() {
    return this.agentUserKnowledge;
  }
  public setAgentUserKnowledge(list: AgentUserKnowledgeFolderData[]) {
    this.agentUserKnowledge = list;
    this.dispatchAgentUserKnowledgeChange();
  }

  public getKnowledgeFolderItems() {
    return this.knowledgeFolderItems;
  }
  public setKnowledgeFolderItems(list: ItemDetailData[]) {
    this.knowledgeFolderItems = list;
    this.dispatchKnowledgeFolderItemsChange();
  }

  // ==================== 订阅通知操作 ====================
  // 监听目录列表变化事件
  private dispatchKnowledgeFoldersChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<KnowledgeFoldersChangeEventDetail>(`${this.STORAGE_KEY}:knowledge-folders-change`, {
      detail: { knowledgeFolders: this.knowledgeFolders }
    });
    window.dispatchEvent(event);
  }
  public onKnowledgeFoldersChange(listener: (e: KnowledgeFoldersChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:knowledge-folders-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听请求表单变化事件
  private dispatchKnowledgeFolderSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<KnowledgeFolderSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:knowledge-form-change`, {
      detail: { knowledgeFolderSearchForm: this.knowledgeFolderSearchForm }
    });
    window.dispatchEvent(event);
  }
  public onKnowledgeFolderSearchFormChange(listener: (e: KnowledgeFolderSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:knowledge-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听用户素材库目录变化事件
  private dispatchAgentUserKnowledgeChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AgentUserKnowledgeChangeEventDetail>(`${this.STORAGE_KEY}:agent-user-knowledge-change`, {
      detail: { agentUserKnowledge: this.agentUserKnowledge }
    });
    window.dispatchEvent(event);
  }
  public onAgentUserKnowledgeChange(listener: (e: AgentUserKnowledgeChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:agent-user-knowledge-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听素材库文件夹内文件列表变化事件
  private dispatchKnowledgeFolderItemsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<KnowledgeFolderItemsChangeEventDetail>(`${this.STORAGE_KEY}:knowledge-folder-items-change`, {
      detail: { knowledgeFolderItems: this.knowledgeFolderItems }
    });
    window.dispatchEvent(event);
  }
  public onKnowledgeFolderItemsChange(listener: (e: KnowledgeFolderItemsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:knowledge-folder-items-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// =============================== Hooks ===============================
// 请求知识库目录数据（用于添加知识库模板列表）
export const useKnowledgeFoldersData = () => {
  const toast = useToast();
  const userId = new URLSearchParams(window.location.search).get("userId");

  const [knowledgeFolders, setKnowledgeFolders] = useState(agentDashboardKnowledgeManager.getKnowledgeFolders());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    try {
      setLoading(true);
      await agentDashboardKnowledgeManager.fetchKnowledgeFolders(undefined, userId);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentDashboardKnowledgeManager.onKnowledgeFoldersChange((e) => {
      setKnowledgeFolders(e.detail.knowledgeFolders)
    })
    return unsubscribe
  }, [])

  return {
    knowledgeFolders,
    loading,
  }
}

// 请求智能体用户知识库
export const useAgentUserKnowledgeData = () => {
  const toast = useToast();
  const params = useParams();
  const _userId = new URLSearchParams(window.location.search).get("userId");

  const [agentUserKnowledge, setAgentUserKnowledge] = useState(agentDashboardKnowledgeManager.getAgentUserKnowledge());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    try {
      setLoading(true);
      await agentDashboardKnowledgeManager.init({
        agentId: params.id,
        userId: _userId,
      });
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const searchAgentUserKnowledge = async () => {
    try {
      setLoading(true);
      await agentDashboardKnowledgeManager.fetchAgentUserKnowledge({
        agentId: params.id,
        userId: _userId,
      });
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentDashboardKnowledgeManager.onAgentUserKnowledgeChange((e) => {
      setAgentUserKnowledge(e.detail.agentUserKnowledge)
    })
    return unsubscribe
  }, [])

  return {
    agentUserKnowledge,
    searchAgentUserKnowledge,
    loading,
  }
}

// 请求智能体用户知识库指定文件夹内文件列表数据
export const useAgentUserKnowledgeItems = () => {
  const toast = useToast();

  const [knowledgeFolderItems, setKnowledgeFolderItems] = useState<ItemDetailData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchKnowledgeFolderItems = async (folderId: number) => {
    try {
      setLoading(true);
      await agentDashboardKnowledgeManager.fetchKnowledgeFolderItems(folderId);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const unsubscribe = agentDashboardKnowledgeManager.onKnowledgeFolderItemsChange((e) => {
      setKnowledgeFolderItems(e.detail.knowledgeFolderItems)
    })
    return unsubscribe
  }, [])

  return {
    knowledgeFolderItems,
    loading,
    fetchKnowledgeFolderItems,
  }
}

// 创建智能体用户知识库
export const useAgentUserKnowledgeCreate = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const agentUserKnowledgeCreate = async (folderId: number[]) => {
    try {
      setLoading(true);

      // 根据 folderId，判断智能体用户知识库列表中已存在的项
      const validIds = folderId
        .filter(id => !agentDashboardKnowledgeManager.getAgentUserKnowledge()
          .some(item => item.folderId === id));

      if (validIds.length === 0) {
        toast.toast({
          description: t('agent_dashboard.user_detail.knowledge.add_modal.save_valid_tips')
        });
        return;
      }

      const params = {
        folderId,
        type: PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
      }
      await agentDashboardKnowledgeManager.agentUserKnowledgeCreate(params);
      // 刷新智能体用户知识库列表
      await agentDashboardKnowledgeManager.fetchAgentUserKnowledge({})
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    agentUserKnowledgeCreate,
    loading,
  }
}

// 删除智能体用户知识库
export const useAgentUserKnowledgeDelete = () => {
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const agentUserKnowledgeDelete = async (id: number) => {
    try {
      setLoading(true);
      await agentDashboardKnowledgeManager.agentUserKnowledgeDelete(id);
      // 刷新智能体用户知识库列表
      await agentDashboardKnowledgeManager.fetchAgentUserKnowledge({});
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    agentUserKnowledgeDelete,
    loading,
  };
};

export const agentDashboardKnowledgeHooks = {
  // 知识库添加弹窗列表数据
  useKnowledgeFoldersData,
  // 智能体用户知识库数据
  useAgentUserKnowledgeData,
  useAgentUserKnowledgeItems,
  useAgentUserKnowledgeCreate,
  useAgentUserKnowledgeDelete,
}

// 导出单例实例
export const agentDashboardKnowledgeManager = AgentDashboardKnowledgeManager.getInstance();
